import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { UserCircle, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../api';
import { formatApiErrorDetail } from './Login';

const Profile = ({ user, authChecked, onUserUpdate }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', phone: '' });
  const [pw, setPw] = useState({ current_password: '', new_password: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    if (user) setForm({ name: user.name || '', phone: user.phone || '' });
  }, [user]);

  useEffect(() => {
    if (authChecked && !user) navigate('/login');
  }, [authChecked, user, navigate]);

  if (!user) return <div className="min-h-screen flex items-center justify-center pt-16"><p className="text-gray-600">Loading...</p></div>;

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.patch('/auth/profile', form);
      onUserUpdate(res.data);
      toast.success('Profile updated');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) { toast.error('New passwords do not match'); return; }
    setChanging(true);
    try {
      await api.post('/auth/change-password', { current_password: pw.current_password || null, new_password: pw.new_password });
      toast.success(user.has_password ? 'Password changed' : 'Password set — you can now log in with email too');
      setPw({ current_password: '', new_password: '', confirm: '' });
      onUserUpdate({ ...user, has_password: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setChanging(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16" data-testid="profile-page">
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-5">
          {user.picture ? <img src={user.picture} alt={user.name} className="w-20 h-20 rounded-full border-4 border-white" /> : <UserCircle className="w-20 h-20" />}
          <div><h1 className="text-4xl font-bold">{user.name}</h1><p className="text-teal-50">{user.email}</p></div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 md:grid-cols-2 gap-8">
        <Card className="border-0 shadow-lg">
          <CardHeader><CardTitle className="flex items-center text-xl"><UserCircle className="w-5 h-5 mr-2 text-teal-600" />Personal details</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={saveProfile} className="space-y-4" data-testid="profile-form">
              <div><Label htmlFor="name" className="mb-1.5 block">Full name</Label><Input data-testid="profile-name-input" id="name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required minLength={2} className="border-2" /></div>
              <div><Label htmlFor="phone" className="mb-1.5 block">Phone (for SMS updates)</Label><Input data-testid="profile-phone-input" id="phone" type="tel" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+965 ..." className="border-2" /></div>
              <div><Label className="mb-1.5 block">Email</Label><Input value={user.email} disabled className="border-2 bg-gray-50" /></div>
              <Button data-testid="profile-save-btn" type="submit" disabled={saving} className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white">{saving ? 'Saving...' : 'Save changes'}</Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardHeader><CardTitle className="flex items-center text-xl"><KeyRound className="w-5 h-5 mr-2 text-teal-600" />{user.has_password ? 'Change password' : 'Set a password'}</CardTitle></CardHeader>
          <CardContent>
            {!user.has_password && <p className="text-sm text-gray-500 mb-4">You signed up with Google. Set a password to also log in with your email.</p>}
            <form onSubmit={changePassword} className="space-y-4" data-testid="password-form">
              {user.has_password && (
                <div><Label className="mb-1.5 block">Current password</Label><Input data-testid="current-password-input" type="password" value={pw.current_password} onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))} required className="border-2" autoComplete="current-password" /></div>
              )}
              <div><Label className="mb-1.5 block">New password</Label><Input data-testid="new-password-input" type="password" value={pw.new_password} onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))} required minLength={8} className="border-2" autoComplete="new-password" /></div>
              <div><Label className="mb-1.5 block">Confirm new password</Label><Input data-testid="confirm-password-input" type="password" value={pw.confirm} onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))} required minLength={8} className="border-2" autoComplete="new-password" /></div>
              <Button data-testid="change-password-btn" type="submit" disabled={changing} variant="outline" className="border-2 border-teal-500 text-teal-600 hover:bg-teal-50">{changing ? 'Updating...' : user.has_password ? 'Change password' : 'Set password'}</Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Profile;
